import * as React from 'react'
import { ArgumentAxis, CommonSeriesSettings, Legend, Series, Tooltip, ValueAxis } from 'devextreme-react/chart'
import { BPMChart } from '../../../../components/Chart'
import { BPMAnalyticsAPI } from '../../../../api'
import { PlatformContext } from '../../../../components/context/platform'
import { Item } from 'devextreme-react/toolbar'
import { createPortal } from 'react-dom'

export const UtilizationChart: React.FunctionComponent = ({ }) => {
    const platform = React.useContext(PlatformContext)

    if (platform !== 'supreme') {
        return null
    }

    const toolbarItems = [<Item location='before' render={() => <UtilizationHelpAllTipPortal />} />]

    return (
        <BPMChart title={"Utilization"} subTitle={'By Plan'} type='bar'
            toolBarItems={() => toolbarItems}
            onRequestLoad={(options, signal) => {
                return BPMAnalyticsAPI.get<any>('downloads/utilization', options, signal)
            }}>

            <ArgumentAxis argumentType="string" />
            <ValueAxis
                name="nu"
                position="left"
                title="Tracks per 10k"
                label={{ customizeText: (e: any) => `${e.valueText}` }}
            />
            <ValueAxis
                name="pct"
                position="right"
                title="%"
                label={{ customizeText: (e: any) => `${e.valueText}%` }}
            />

            <CommonSeriesSettings
                argumentField="plan_name"
                hoverMode="allArgumentPoints"
            />

            {/* Primary metric: normalized utilization */}
            <Series
                type="bar"
                name="NU per 10k"
                valueField="nu_per_10k"
                axis="nu"
            />

            {/* Secondary metrics (toggle via legend) */}
            <Series
                type="spline"
                name="Catalog Reach %"
                valueField="ccr_pct"
                axis="pct"
            />
            <Series
                type="spline"
                name="New-Track Reach %"
                valueField="ntr_pct"
                axis="pct"
            />

            <Legend verticalAlignment="bottom" horizontalAlignment="center" visible />

            <Tooltip
                enabled
                customizeTooltip={(info: any) => {
                    const d = info.point?.data || {};
                    const fmtPct = (v: any) =>
                        v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`;
                    const fmtInt = (v: any) =>
                        v === null || v === undefined ? '—' : Number(v).toLocaleString();

                    return {
                        html: `
              <div style="min-width:240px">
                <div><b>${d.plan_name ?? ''}</b></div>
                <div>Library size: ${fmtInt(d.library_size)}</div>
                <hr style="border:none;border-top:1px solid #eee;margin:6px 0" />
                <div>Users w/ downloads: ${fmtInt(d.users_with_downloads)}</div>
                <div>Avg downloads / user: ${fmtInt(d.avg_downloads_per_user)}</div>
                <div>Avg distinct tracks / user: ${fmtInt(d.avg_distinct_tracks_per_user)}</div>
                <hr style="border:none;border-top:1px solid #eee;margin:6px 0" />
                <div><b>NU per 10k:</b> ${fmtInt(d.nu_per_10k)}</div>
                <div>Catalog Reach: ${fmtPct(d.ccr_pct)}</div>
                <div>New-Track Reach: ${fmtPct(d.ntr_pct)}</div>
              </div>
            `,
                    };
                }}
            />

        </BPMChart>
    )
}

type Props = {
    className?: string;
    title?: string;
    placement?: 'bottom' | 'top' | 'right' | 'left';
};

export function UtilizationHelpAllTipPortal({
    className,
    title = 'Utilization metrics (selected period & filters)',
    placement = 'bottom',
}: Props) {
    const [open, setOpen] = React.useState(false);
    const [pos, setPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = React.useRef<HTMLSpanElement | null>(null);
    const tipRef = React.useRef<HTMLDivElement | null>(null);
    const id = 'help';

    const items: Array<{ label: string; desc: string; formula?: string }> = [
        { label: 'Library size', desc: 'Distinct tracks in the filtered library as of period end (uploaded_at ≤ end).' },
        { label: 'Users with downloads', desc: 'Distinct users on this plan who downloaded ≥1 track in the period (not total subscribers).' },
        { label: 'Avg downloads / user', desc: 'Average total downloads per active downloader (duplicates count).' },
        { label: 'Avg distinct tracks / user', desc: 'Average unique tracks per active downloader (de-duplicated by track).' },
        { label: 'NU per 10k', desc: 'Normalized utilization: typical user’s distinct coverage per 10k tracks (scales across library sizes).', formula: '(avg_distinct_tracks_per_user ÷ library_size) × 10,000' },
        { label: 'Catalog Reach %', desc: 'Cohort catalog reach: share of the library touched by any user on this plan.', formula: '(distinct_tracks_touched_by_plan ÷ library_size) × 100' },
        { label: 'New-Track Reach %', desc: 'Share of tracks uploaded in the window that were downloaded at least once by the plan cohort.', formula: '(distinct_new_tracks_touched ÷ new_tracks_added_in_window) × 100' },
    ];

    const computePosition = React.useCallback(() => {
        const t = triggerRef.current;
        const el = tipRef.current;
        if (!t || !el) return;

        const rect = t.getBoundingClientRect();
        const tipRect = el.getBoundingClientRect();
        const margin = 10;

        let top = 0;
        let left = 0;

        switch (placement) {
            case 'top':
                top = rect.top - tipRect.height - margin;
                left = rect.left + rect.width / 2 - tipRect.width / 2;
                break;
            case 'right':
                top = rect.top + rect.height / 2 - tipRect.height / 2;
                left = rect.right + margin;
                break;
            case 'left':
                top = rect.top + rect.height / 2 - tipRect.height / 2;
                left = rect.left - tipRect.width - margin;
                break;
            case 'bottom':
            default:
                top = rect.bottom + margin;
                left = rect.left + rect.width / 2 - tipRect.width / 2;
                break;
        }

        // Clamp to viewport with 8px padding
        const pad = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        top = Math.max(pad, Math.min(top, vh - tipRect.height - pad));
        left = Math.max(pad, Math.min(left, vw - tipRect.width - pad));

        setPos({ top, left });
    }, [placement]);

    React.useEffect(() => {
        if (!open) return;
        computePosition();
        const onScroll = () => computePosition();
        const onResize = () => computePosition();
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };

        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [open, computePosition]);

    // Recompute on open after first render of the tip
    React.useLayoutEffect(() => {
        if (open) computePosition();
    }, [open, computePosition]);

    const tip = open ? createPortal(
        <div
            ref={tipRef}
            id={id}
            role="tooltip"
            style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                background: '#111',
                color: '#fff',
                padding: '10px 12px',
                borderRadius: 8,
                fontSize: 12.5,
                lineHeight: 1.45,
                boxShadow: '0 12px 28px rgba(0,0,0,.28)',
                maxWidth: 440,
                zIndex: 10000,
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {title}
            </div>
            <dl style={{ margin: 0 }}>
                {items.map((it) => (
                    <div key={it.label} style={{ marginBottom: 8 }}>
                        <dt style={{ fontWeight: 600, display: 'inline' }}>{it.label}:</dt>{' '}
                        <dd style={{ display: 'inline', margin: 0 }}>{it.desc}</dd>
                        {it.formula && (
                            <div style={{ marginTop: 2, fontSize: 11.5 }}>
                                <span style={{ opacity: 0.75 }}>Formula:</span>{' '}
                                <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 4px', borderRadius: 4 }}>
                                    {it.formula}
                                </code>
                            </div>
                        )}
                    </div>
                ))}
            </dl>
        </div>,
        document.body
    ) : null;

    return (
        <span
            ref={triggerRef}
            className={className}
            style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onClick={() => setOpen(v => !v)}
            tabIndex={0}
            aria-describedby={id}
        >
            <InfoIcon />
            {tip}
        </span>
    );
}

function InfoIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
            style={{ display: 'inline-block', marginLeft: 6, cursor: 'help', fill: 'currentColor', opacity: 0.9 }}
        >
            <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12" />
            <path d="M11 10h2v8h-2zM11 6h2v2h-2z" />
        </svg>
    );
}



// WEBPACK FOOTER //
// ./src/pages/Statistics/Downloads/charts/UtilizationChart.tsx